import { isEqual } from 'lodash';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AcresFeatureFlags, LOCAL_STORAGE_THEME } from '@/constants/constants';
import { getFeatures } from '@/endpoints/itasca/features/getFeatureFlags';
import LoadingScreen from '@/features/loadingScreen';
import useWebStorage from '@/hooks/useWebStorage';

import { useAuth } from './auth-provider';

enum Themes {
  LIGHT = 'light',
  DARK = 'dark',
}
interface IProps {
  children: ReactNode;
}

export interface IFeatureContext {
  [key: string]: string | boolean;
}

type FeatureFlagObj = {
  [key: string]: string | boolean | number | Object | null;
};

interface Context {
  itascaFeatureFlags: FeatureFlagObj | null;
  toggleTheme: () => void;
  getFeature: (
    key: AcresFeatureFlags
  ) => string | boolean | number | Object | null;
}

const POLLING_INTERVAL = 60000;

export const InitializeContext = createContext<Context>({
  getFeature: () => null,
  itascaFeatureFlags: null,
  toggleTheme: () => {},
});

const InitializeProvider = ({ children }: IProps) => {
  const { authStateLoading } = useAuth();

  const [itascaFeatureFlags, setItascaFeatureFlags] =
    useState<FeatureFlagObj | null>(null);

  const { savedValue, updateValue } = useWebStorage<Themes>({
    key: LOCAL_STORAGE_THEME,
  });

  const toggleTheme = useCallback(() => {
    if (savedValue === Themes.LIGHT) {
      updateValue(Themes.DARK);
    } else {
      updateValue(Themes.LIGHT);
    }
  }, [savedValue, updateValue]);

  const isDarkMode = useMemo(() => savedValue === Themes.DARK, [savedValue]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add(Themes.DARK);
    } else {
      document.documentElement.classList.remove(Themes.DARK);
    }
  }, [isDarkMode]);

  const getFeatureFlagsFromItasca = useCallback(async () => {
    const { res } = await getFeatures();

    if (!res) return;

    const flags =
      res?.data?.results.map((flag) => {
        const { key, value, deprecated } = flag;

        return { key, value: deprecated ? false : value };
      }) ?? [];

    const featureFlagMap = flags.reduce(
      // eslint-disable-next-line no-return-assign
      (obj, item) => Object.assign(obj, { [item.key]: item.value }),
      {}
    );

    // Since we are making a new object, this causes rerenders everytime its changed
    //  only setting to the new value if the object is NOT EQUIVALENT to the previous
    setItascaFeatureFlags((prev) => {
      if (!isEqual(featureFlagMap, prev)) {
        return featureFlagMap;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (!authStateLoading) {
      getFeatureFlagsFromItasca();
      interval = setInterval(() => {
        getFeatureFlagsFromItasca();
      }, POLLING_INTERVAL);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [authStateLoading, getFeatureFlagsFromItasca]);

  const getFeature = useCallback(
    (key: AcresFeatureFlags) => {
      return itascaFeatureFlags?.[key] ?? null;
    },
    [itascaFeatureFlags]
  );

  const value = useMemo(() => {
    return {
      getFeature,
      isDarkMode,
      itascaFeatureFlags,
      theme: savedValue,
      toggleTheme,
    };
  }, [getFeature, isDarkMode, itascaFeatureFlags, savedValue, toggleTheme]);

  return (
    <InitializeContext.Provider value={value}>
      {children}
      {!itascaFeatureFlags && <LoadingScreen />}
    </InitializeContext.Provider>
  );
};

export default InitializeProvider;
export const useInitializeContext = () => useContext(InitializeContext);
